<template>
  <div class="user-dashboard">
    <!-- Dashboard Style Topbar Header -->
    <div class="dashboard-sticky-area">
      <div class="dashboard-topbar">
        <h4 class="dashboard-page-title">
          <CIcon name="cil-speedometer" class="dashboard-title-icon" />
          <span v-html="$t('mainuser.pageTitle')"></span>
        </h4>

        <div class="dashboard-header-right">
          <button class="report-btn hero-action" @click="checkBeforeReport">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            <span>{{ $t('mainuser.createNewRequest') }}</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 3 Key Stat Cards (Attached KPI Grid) -->
    <HomeStatCards :cards="statCards" :active-key="selectedStatus" @select="selectedStatus = $event" />

    <CCard class="details-card mb-4">
      <CCardHeader class="details-card-header">
        <div>
          <h4 class="mb-0">{{ activeTableTitle }}</h4>
        </div>
      </CCardHeader>
      <CCardBody>
        <Ureportable
          ref="activeReportTable"
          mode="unfinished"
          ticket-source="user"
          :status-filter="selectedStatus"
          :date-range="selectedRange"
          :start-date="startDate"
          :end-date="endDate"
          :search-query="searchQuery"
          @tickets-loaded="updateStats"
          @show-detail="handleShowDetail"
        />
      </CCardBody>
    </CCard>

    <TicketDetailModal v-if="selectedReport" @close="closeDetail">
      <UReportDetail :report="selectedReport" @close="closeDetail" @refresh="refreshTables"
        @submit-evaluation="handleEvaluationSubmit" />
    </TicketDetailModal>
  </div>
</template>

<script>
import HomeStatCards from "@/components/common/HomeStatCards.vue";
import Ureportable from "../../components/user/Ureportable.vue";
import UReportDetail from "./UReportDetail.vue";
import TicketDetailModal from '@/components/common/TicketDetailModal.vue';
import Swal from "sweetalert2";
import { getSocket } from "../../service/socketio.service";
import Service from '@/service/api'

export default {
  name: "Dashboard",
  components: {
    HomeStatCards,
    Ureportable,
    UReportDetail,
    TicketDetailModal,
  },
  props: {
  },
  data() {
    return {
      selectedReport: null,
      selectedStatus: 'total',
      selectedRange: 'all',
      startDate: '',
      endDate: '',
      searchQuery: '',
      showSearchDropdown: false,
      currentMatchIndex: 0,
      loadedTickets: [],
      stats: {
        total: 0,
        completed: 0,
        awaitingEval: 0,
      },
    };
  },
  computed: {
    rangeOptions() {
      return [
        { label: this.dashboardText("สัปดาห์นี้", "Week"), value: "week" },
        { label: this.dashboardText("เดือนนี้", "Month"), value: "month" },
        { label: this.dashboardText("ปีนี้", "Year"), value: "year" },
        { label: this.dashboardText("ทั้งหมด", "All time"), value: "all" },
        { label: "", value: "custom" },
      ];
    },
    searchMatches() {
      if (!this.searchQuery || !this.searchQuery.trim()) return [];
      const q = this.searchQuery.trim().toLowerCase();
      const results = [];
      this.loadedTickets.forEach((ticket) => {
        const ticketNo = String(ticket.ticketNumber || ticket._id || "").toLowerCase();
        const reporter = String(ticket.userPart?.name || ticket.userPart?.username || "").toLowerCase();
        const prob = String(ticket.userPart?.problemDetails || "").toLowerCase();
        const dept = String(ticket.userPart?.category?.departmentName || ticket.userPart?.category?.name || ticket.userPart?.category || "").toLowerCase();
        const cat = String(ticket.userPart?.subCategory?.name || ticket.userPart?.subCategory || "").toLowerCase();
        const status = String(ticket.status || "").toLowerCase();

        if (ticketNo.includes(q) || reporter.includes(q) || prob.includes(q) || dept.includes(q) || cat.includes(q) || status.includes(q)) {
          results.push({
            id: 'ticket-' + (ticket._id || ticket.ticketNumber),
            ticket,
            sectionLabel: this.dashboardText('ใบแจ้งซ่อม', 'Ticket'),
            badgeClass: 'badge-ticket',
            title: `#${ticket.ticketNumber || '-'} — ${ticket.userPart?.name || this.dashboardText('ผู้แจ้ง', 'Reporter')}`,
            description: ticket.userPart?.problemDetails || '-'
          });
        }
      });
      return results;
    },
    activeTableTitle() {
      const isEn = (this.$i18n?.locale || this.$store?.state?.locale?.lang || '').toLowerCase().startsWith('en');
      const baseTitle = this.$t('mainuser.activeRequestsTitle') || (isEn ? 'Maintenance Requests' : 'รายการคำขอแจ้งซ่อม');
      const suffixMap = {
        total: isEn ? 'Total Reported' : 'งานแจ้งทั้งหมด',
        completed: isEn ? 'Completed' : 'งานที่เสร็จ',
        awaitingEval: isEn ? 'Awaiting Evaluation' : 'งานที่ค้างประเมิน',
        all: isEn ? 'All' : 'ทั้งหมด',
      };
      const suffix = suffixMap[this.selectedStatus];
      return suffix ? `${baseTitle} — ${suffix}` : baseTitle;
    },
    statCards() {
      return [
        {
          key: "total",
          title: this.$t("home.kpiTotalReported") || "งานแจ้งทั้งหมด",
          value: this.stats.total,
          icon: "cil-bell",
          iconClass: "icon-blue",
          badge: "TOTAL",
          badgeClass: "trend-neutral",
        },
        {
          key: "completed",
          title: this.$t("home.kpiCompleted") || "งานที่เสร็จ",
          value: this.stats.completed,
          icon: "cil-check-circle",
          iconClass: "icon-green",
          badge: "DONE",
          badgeClass: "trend-up",
        },
        {
          key: "awaitingEval",
          title: this.$t("home.kpiAwaitingEval") || "งานที่ค้างประเมิน",
          value: this.stats.awaitingEval,
          icon: "cil-task",
          iconClass: "icon-purple",
          badge: "WAIT",
          badgeClass: "trend-purple",
        },
      ];
    },
  },
  watch: {
    '$route.query.refreshNewRequest'() {
      this.watchRouteQuery();
    },
    '$route.query.openTicketId'() {
      this.watchRouteQuery();
    }
  },
  mounted() {
    this.setupSocketListeners();
    this.watchRouteQuery();
    window.addEventListener('keydown', this.handleGlobalKeydown);
    document.addEventListener('click', this.handleClickOutside);
  },
  beforeDestroy() {
    const socket = getSocket();
    if (socket) {
      socket.off("user_ticket_updated");
    }
    window.removeEventListener('keydown', this.handleGlobalKeydown);
    document.removeEventListener('click', this.handleClickOutside);
  },
  methods: {
    dashboardText(thaiText, englishText) {
      const locale = String(this.$i18n?.locale || this.$store?.state?.locale?.lang || 'th').toLowerCase();
      return locale.startsWith('th') ? thaiText : englishText;
    },
    selectRange(range) {
      this.selectedRange = range;
      if (range !== 'custom') {
        this.startDate = '';
        this.endDate = '';
      }
    },
    onCustomDateChange() {
      if (this.startDate && this.endDate) {
        this.selectedRange = 'custom';
      }
    },
    nextMatch() {
      if (!this.searchMatches.length) return;
      this.currentMatchIndex = (this.currentMatchIndex + 1) % this.searchMatches.length;
      this.jumpToMatch(this.currentMatchIndex);
    },
    prevMatch() {
      if (!this.searchMatches.length) return;
      this.currentMatchIndex = (this.currentMatchIndex - 1 + this.searchMatches.length) % this.searchMatches.length;
      this.jumpToMatch(this.currentMatchIndex);
    },
    jumpToMatch(idx) {
      if (!this.searchMatches[idx]) return;
      this.currentMatchIndex = idx;
      const match = this.searchMatches[idx];
      this.showSearchDropdown = false;
      if (match.ticket) {
        this.handleShowDetail(match.ticket);
      }
    },
    clearSearch() {
      this.searchQuery = '';
      this.showSearchDropdown = false;
      this.currentMatchIndex = 0;
    },
    closeSearchDropdown() {
      this.showSearchDropdown = false;
    },
    handleGlobalKeydown(e) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
        const input = this.$refs.searchBoxRef?.querySelector('input');
        if (input) {
          e.preventDefault();
          input.focus();
          input.select();
          this.showSearchDropdown = true;
        }
      }
    },
    handleClickOutside(e) {
      if (this.$refs.searchBoxRef && !this.$refs.searchBoxRef.contains(e.target)) {
        this.showSearchDropdown = false;
      }
    },
    async fetchStats() {
      if (this.$refs.activeReportTable) {
        this.$refs.activeReportTable.loadTickets();
      }
    },
    updateStats(tickets) {
      const source = Array.isArray(tickets) ? tickets : [];
      this.loadedTickets = source;
      const awaitingEval = source.filter(t => t.status === "done").length;
      const completed = source.filter(t => ["done", "welldone"].includes(t.status)).length;
      const total = source.filter(t => !["cancelled", "cancel"].includes(t.status)).length;
      this.stats = { total, completed, awaitingEval };
    },
    setupSocketListeners() {
      const socket = getSocket();
      if (socket) {
        socket.on("user_ticket_updated", (data) => {
          this.refreshTables();
          
          if (this.selectedReport && this.selectedReport._id === data._id) {
            if (data.status === 'cancelled') {
              this.selectedReport = null;
              Swal.fire({
                icon: 'info',
                title: this.$t('mainuser.ticketCancelledTitle'),
                text: this.$t('mainuser.ticketCancelledText'),
                confirmButtonColor: '#3085d6',
              });
            } else {
              this.selectedReport = { ...this.selectedReport, ...data };
            }
          }
        });
      }
    },
    refreshTables() {
      this.fetchStats();
      this.handleTicketCreated();
    },
    handleTicketCreated() {
      const table = this.$refs.activeReportTable;
      if (table && typeof table.loadTickets === 'function') table.loadTickets();
    },
    async checkBeforeReport() {
      const userStr = localStorage.getItem('user');
      let user = null;
      try {
        user = userStr ? JSON.parse(userStr) : null;
      } catch (e) {
        user = null;
      }
      const userId = user ? user._id : null;
      if (!userId) {
        this.$router.push('/user/new-request');
        return;
      }
      if (user.personClassResolutionStatus === 'conflict') {
        Swal.fire({
          icon: 'warning',
          title: this.$t('sendReport.swalErrorTitle') || 'ไม่สามารถส่งคำขอซ่อมได้',
          text: this.$t('sendReport.conflictAffiliationWarning') || 'พบข้อมูลสถานะบุคคลขัดแย้งกัน (นักศึกษา/บุคลากร) โปรดติดต่อผู้ดูแลระบบ',
          confirmButtonColor: 'rgba(37, 99, 235, 0.85)',
        });
        return;
      }
      try {
        const response = await Service.ticket('get', null, { urlAppend: `/user/${userId}/active` });
        if (response.status === 200) {
          const data = response.data;
          if (data.success && Array.isArray(data.data)) {
            const blockingTicket = data.data.find(ticket => ticket.status === 'done');
            if (blockingTicket) {
              Swal.fire({
                icon: 'warning',
                title: this.$t('mainuser.evalBeforeNewTitle'),
                text: this.$t('mainuser.evalBeforeNewText'),
                confirmButtonColor: 'rgba(37, 99, 235, 0.85)',
                confirmButtonText: this.$t('login.ok'),
                returnFocus: false
              }).then(() => {
                this.handleShowDetail(blockingTicket);
              });
              return;
            }
          }
        }
      } catch (err) {
        console.error("Error checking unevaluated tickets", err);
      }
      this.$router.push('/user/new-request');
    },
    handleShowDetail(item) {
      this.selectedReport = { ...item };

      this.$nextTick(() => {
        const detailCard = document.querySelector(".mt-4");
        if (detailCard) {
          detailCard.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },

    closeDetail() {
      this.selectedReport = null;
    },
    watchRouteQuery() {
      if (this.$route.query.refreshNewRequest === '1') {
        this.handleTicketCreated();
        const query = { ...this.$route.query };
        delete query.refreshNewRequest;
        this.$router.replace({ query }).catch(() => {});
      }
      if (this.$route.query.openTicketId) {
        const ticketId = this.$route.query.openTicketId;
        const target = this.loadedTickets.find(t => String(t._id || t.id) === String(ticketId));
        if (target) {
          this.handleShowDetail(target);
        } else {
          Service.ticket('get', null, { urlAppend: `/${ticketId}` })
            .then(res => {
              if (res.data?.success && res.data.data) {
                this.handleShowDetail(res.data.data);
              }
            })
            .catch(() => {});
        }
        const query = { ...this.$route.query };
        delete query.openTicketId;
        this.$router.replace({ query }).catch(() => {});
      }
    },
    openManual() {
      this.$router.push('/user/manual');
    },

    async handleEvaluationSubmit(data) {
      console.log("Evaluation received:", data);

      await Swal.fire({
        icon: "success",
        title: this.$t('mainuser.thanksEvalTitle'),
        html: `
        <div style="text-align:left; font-size:14px;">
          <p><strong>${this.$t('mainuser.evalTicketNo')}</strong> ${data.ticket_number}</p>
          <p><strong>${this.$t('mainuser.evalScore')}</strong> ${data.rating} ${this.$t('mainuser.evalStars')}</p>
          <p><strong>${this.$t('mainuser.evalComment')}</strong> ${data.comment || this.$t('mainuser.evalNoComment')}</p>
        </div>
      `,
        confirmButtonText: this.$t('login.ok'),
        confirmButtonColor: "rgba(37, 99, 235, 0.85)",
      });

      this.closeDetail();
    }
  }

};
</script>

<style scoped>
.user-dashboard {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

.dashboard-sticky-area {
  position: sticky !important;
  top: 76px !important;
  z-index: 1020 !important;
  margin: 0 0 20px 0 !important;
  padding: 0 0 6px 0 !important;
  width: 100%;
  background: #ebedef !important;
}

.dashboard-topbar {
  width: 100%;
  min-width: 0;
  height: 112px !important;
  min-height: 112px !important;
  margin: 0 !important;
  padding: 22px 28px !important;
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: space-between !important;
  gap: 16px !important;
  flex-wrap: nowrap !important;
  background: #1e40af !important;
  border: 0 !important;
  border-radius: 12px !important;
  box-sizing: border-box !important;
  color: #fff !important;
}

.dashboard-page-title {
  margin: 0 !important;
  padding: 0 !important;
  display: flex !important;
  align-items: center !important;
  gap: 16px !important;
  color: #ffffff !important;
  font-size: 1.6rem !important;
  font-weight: 800 !important;
  letter-spacing: -0.02em !important;
  line-height: 1.2 !important;
  min-width: 0 !important;
  flex: 1 1 auto !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
}

.dashboard-title-icon,
.dashboard-page-title svg,
::v-deep .dashboard-page-title .c-icon {
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  width: 54px !important;
  height: 54px !important;
  flex: 0 0 54px !important;
  border-radius: 16px !important;
  background: rgba(255, 255, 255, 0.16) !important;
  color: #ffffff !important;
  padding: 11px !important;
  margin: 0 !important;
}

.dashboard-filter {
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-shrink: 0;
  gap: 8px;
}

.report-btn,
.hero-action {
  background: #ffffff !important;
  border: none !important;
  color: #1e40af !important;
  min-height: 48px !important;
  padding: 10px 24px !important;
  border-radius: 50px !important;
  font-size: 15px !important;
  font-weight: 700 !important;
  display: inline-flex !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8px !important;
  cursor: pointer !important;
  white-space: nowrap !important;
  flex-shrink: 0 !important;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12) !important;
  transition: all 0.2s ease !important;
}

.report-btn:hover,
.hero-action:hover {
  background: #fdfdfd !important;
  transform: translateY(-1px) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2) !important;
}

.details-card {
  border-radius: 16px !important;
  overflow: hidden;
  border: 1px solid rgba(37, 99, 235, 0.12);
  box-shadow: 0 4px 20px rgba(37, 99, 235, 0.06);
}

.details-card-header {
  background: #f8fafc !important;
  border-bottom: 1px solid rgba(37, 99, 235, 0.08) !important;
  color: #1e40af !important;
  border-bottom: 1px solid rgba(37, 99, 235, 0.1);
  padding: 18px 22px;
}

.details-card-header h4 {
  margin-bottom: 0;
  color: #1e40af;
  font-weight: 800;
}

::v-deep .card-btn {
  border-radius: 8px !important;
  box-shadow: none !important;
}

.dashboard-header-right {
  display: flex !important;
  flex-direction: row !important;
  align-items: center !important;
  justify-content: flex-end !important;
  width: auto !important;
  flex-shrink: 0 !important;
  margin-left: auto !important;
}

@media (max-width: 1200px) {
  .dashboard-sticky-area {
    top: 0 !important;
    margin: 0 0 16px 0 !important;
    padding: 4px 0 6px 0 !important;
    background: #ebedef !important;
  }
}

@media (max-width: 768px) {
  .dashboard-sticky-area {
    top: 0 !important;
    margin: 0 0 12px 0 !important;
    padding: 2px 0 6px 0 !important;
    background: #ebedef !important;
  }
  .dashboard-topbar {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: space-between !important;
    flex-wrap: nowrap !important;
    height: auto !important;
    min-height: 88px !important;
    padding: 14px 18px !important;
    border-radius: 12px !important;
    gap: 12px !important;
    margin: 0 !important;
  }
  .dashboard-header-right {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: flex-end !important;
    width: auto !important;
    flex-shrink: 0 !important;
    margin-left: auto !important;
  }
  .dashboard-page-title {
    font-size: 1.3rem !important;
    gap: 12px !important;
  }
  .dashboard-title-icon,
  .dashboard-page-title svg,
  ::v-deep .dashboard-page-title .c-icon {
    width: 46px !important;
    height: 46px !important;
    flex: 0 0 46px !important;
    border-radius: 13px !important;
    padding: 9px !important;
  }
  .report-btn,
  .hero-action {
    min-height: 44px !important;
    padding: 8px 18px !important;
    font-size: 13.5px !important;
    gap: 6px !important;
    white-space: nowrap !important;
    flex-shrink: 0 !important;
  }
  .details-card-header { padding: 14px 16px; }
  .details-card-header h4 { font-size: 1rem; }
  .details-card .card-body { padding: 12px; }
}

@media (max-width: 480px) {
  .dashboard-topbar {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: space-between !important;
    flex-wrap: nowrap !important;
    height: auto !important;
    min-height: 84px !important;
    padding: 12px 16px !important;
    border-radius: 12px !important;
    gap: 10px !important;
    margin: 0 !important;
  }
  .dashboard-header-right {
    display: flex !important;
    flex-direction: row !important;
    align-items: center !important;
    justify-content: flex-end !important;
    width: auto !important;
    flex-shrink: 0 !important;
    margin-left: auto !important;
  }
  .dashboard-page-title {
    font-size: 1.15rem !important;
    gap: 10px !important;
  }
  .dashboard-title-icon,
  .dashboard-page-title svg,
  ::v-deep .dashboard-page-title .c-icon {
    width: 42px !important;
    height: 42px !important;
    flex: 0 0 42px !important;
    border-radius: 12px !important;
    padding: 8px !important;
  }
  .report-btn,
  .hero-action {
    min-height: 40px !important;
    padding: 7px 14px !important;
    font-size: 13px !important;
    gap: 5px !important;
    white-space: nowrap !important;
    flex-shrink: 0 !important;
  }
}
</style>
